import {
  Scene,
  Vector3,
  Color3,
  AbstractMesh,
  MeshBuilder,
  StandardMaterial,
  LinesMesh,
  SceneLoader,
  DynamicTexture
} from '@babylonjs/core';

export class DebugRenderer {
  private static instance: DebugRenderer | null = null;
  private scene: Scene;
  private meshes: Set<AbstractMesh> = new Set();
  private textPlanes: Set<AbstractMesh> = new Set();
  
  // Configuration
  private defaultSplineMarkerInterval: number = 1.0;
  private defaultArrowLength: number = 0.5;
  private defaultArrowColor: Color3 = new Color3(1, 1, 0); // Yellow
  private defaultLineColor: Color3 = new Color3(0, 1, 1); // Cyan
  private defaultWireframeColor: Color3 = new Color3(0, 1, 0); // Green
  
  private constructor(scene: Scene) {
    this.scene = scene;
  }
  
  /**
   * Mark a mesh as a debug mesh - excludes it from shadows and physics.
   * Shadow exclusion is handled by filterMesh callback on ShadowGenerators
   * which checks the DEBUG_LAYER_MASK (0x20000000).
   */
  private markAsDebugMesh(mesh: AbstractMesh): void {
    mesh.layerMask = 0x20000000; // Debug layer - filterMesh excludes from shadows
    mesh.isPickable = false;
    mesh.checkCollisions = false;
    mesh.receiveShadows = false;
    mesh.metadata = { ...(mesh.metadata || {}), isDebug: true, noShadows: true };
  }
  
  public static getInstance(scene?: Scene): DebugRenderer {
    if (!DebugRenderer.instance) {
      if (!scene) {
        throw new Error('DebugRenderer: Scene required for first initialization');
      }
      DebugRenderer.instance = new DebugRenderer(scene);
    }
    return DebugRenderer.instance;
  }
  
  public createLine(points: Vector3[], color?: Color3): LinesMesh {
    const lineColor = color || this.defaultLineColor;
    
    const line = MeshBuilder.CreateLines(
      `debugLine_${Date.now()}`,
      { points },
      this.scene
    );
    
    line.color = lineColor;
    this.markAsDebugMesh(line);
    
    this.meshes.add(line);
    return line;
  }
  
  public createArrow(origin: Vector3, direction: Vector3, length: number, color?: Color3): AbstractMesh {
    const arrowColor = color || this.defaultArrowColor;
    const normalizedDir = direction.normalize();
    
    // Create cylinder for shaft
    const shaftLength = length * 0.7;
    const shaft = MeshBuilder.CreateCylinder(
      `debugArrowShaft_${Date.now()}`,
      {
        height: shaftLength,
        diameter: 0.05,
        tessellation: 8
      },
      this.scene
    );
    
    // Create cone for head
    const headLength = length * 0.3;
    const head = MeshBuilder.CreateCylinder(
      `debugArrowHead_${Date.now()}`,
      {
        diameterTop: 0,
        diameterBottom: 0.15,
        height: headLength,
        tessellation: 8
      },
      this.scene
    );
    
    // Position head at end of shaft
    head.position.y = (shaftLength + headLength) / 2;
    head.parent = shaft;
    
    // Create material
    const material = new StandardMaterial(`debugArrowMat_${Date.now()}`, this.scene);
    material.emissiveColor = arrowColor;
    material.disableLighting = true;
    shaft.material = material;
    head.material = material;
    
    // Mark both meshes as debug (no shadows, no collisions, etc.)
    this.markAsDebugMesh(shaft);
    this.markAsDebugMesh(head);
    
    // Position and orient the arrow
    shaft.position.copyFrom(origin);
    
    // Calculate rotation to align with direction
    const up = Vector3.Up();
    const dot = Vector3.Dot(up, normalizedDir);
    const angle = Math.acos(Math.max(-1, Math.min(1, dot))); // Clamp to avoid NaN
    const axis = Vector3.Cross(up, normalizedDir);
    
    shaft.rotationQuaternion = null;
    if (axis.length() > 0.001) {
      // Normal case: rotate around the cross product axis
      shaft.rotate(axis.normalize(), angle);
    } else if (dot < 0) {
      // Special case: direction is exactly opposite to up (pointing down)
      // Rotate 180 degrees around any perpendicular axis (e.g., X or Z)
      shaft.rotate(Vector3.Right(), Math.PI);
    }
    // If dot >= 0 and axis is zero, direction is same as up - no rotation needed
    
    // Move to midpoint
    shaft.position.addInPlace(normalizedDir.scale(shaftLength / 2));
    
    this.meshes.add(shaft);
    return shaft;
  }
  
  public createSphere(position: Vector3, radius: number, color?: Color3, wireframe: boolean = true): AbstractMesh {
    const sphereColor = color || this.defaultWireframeColor;
    
    const sphere = MeshBuilder.CreateSphere(
      `debugSphere_${Date.now()}`,
      {
        diameter: radius * 2,
        segments: 16
      },
      this.scene
    );
    
    sphere.position.copyFrom(position);
    
    const material = new StandardMaterial(`debugSphereMat_${Date.now()}`, this.scene);
    material.emissiveColor = sphereColor;
    material.disableLighting = true;
    material.wireframe = wireframe;
    sphere.material = material;
    
    this.markAsDebugMesh(sphere);
    
    this.meshes.add(sphere);
    return sphere;
  }
  
  public createBox(position: Vector3, size: Vector3, color?: Color3, wireframe: boolean = true): AbstractMesh {
    const boxColor = color || this.defaultWireframeColor;
    
    const box = MeshBuilder.CreateBox(
      `debugBox_${Date.now()}`,
      {
        width: size.x,
        height: size.y,
        depth: size.z
      },
      this.scene
    );
    
    box.position.copyFrom(position);
    
    const material = new StandardMaterial(`debugBoxMat_${Date.now()}`, this.scene);
    material.emissiveColor = boxColor;
    material.disableLighting = true;
    material.wireframe = wireframe;
    box.material = material;
    
    this.markAsDebugMesh(box);
    
    this.meshes.add(box);
    return box;
  }
  
  public createCapsule(position: Vector3, height: number, radius: number, color?: Color3, wireframe: boolean = true): AbstractMesh {
    const capsuleColor = color || this.defaultWireframeColor;
    
    const capsule = MeshBuilder.CreateCapsule(
      `debugCapsule_${Date.now()}`,
      {
        height,
        radius,
        tessellation: 16
      },
      this.scene
    );
    
    capsule.position.copyFrom(position);
    
    const material = new StandardMaterial(`debugCapsuleMat_${Date.now()}`, this.scene);
    material.emissiveColor = capsuleColor;
    material.disableLighting = true;
    material.wireframe = wireframe;
    material.alpha = wireframe ? 1.0 : 0.3; // Semi-transparent if filled
    capsule.material = material;
    
    capsule.material = material;
    
    this.markAsDebugMesh(capsule);
    
    this.meshes.add(capsule);
    return capsule;
  }
  
  public createBoundingBoxWireframe(mesh: AbstractMesh, color?: Color3): AbstractMesh {
    const boxColor = color || this.defaultWireframeColor;
    
    // Get bounding info from mesh
    const boundingInfo = mesh.getBoundingInfo();
    const size = boundingInfo.boundingBox.extendSize.scale(2);
    const center = boundingInfo.boundingBox.center;
    
    // Create wireframe box at bounding box center with same size
    const box = MeshBuilder.CreateBox(
      `debugBoundingBox_${Date.now()}`,
      {
        width: size.x,
        height: size.y,
        depth: size.z
      },
      this.scene
    );
    
    // Position relative to mesh's world transform
    const worldCenter = Vector3.TransformCoordinates(center, mesh.getWorldMatrix());
    box.position.copyFrom(worldCenter);
    box.rotationQuaternion = mesh.rotationQuaternion ? mesh.rotationQuaternion.clone() : null;
    if (!box.rotationQuaternion) {
      box.rotation.copyFrom(mesh.rotation);
    }
    
    const material = new StandardMaterial(`debugBoundingBoxMat_${Date.now()}`, this.scene);
    material.emissiveColor = boxColor;
    material.disableLighting = true;
    material.wireframe = true;
    box.material = material;
    
    this.markAsDebugMesh(box);
    
    this.meshes.add(box);
    return box;
  }
  
  public async createAsset(url: string): Promise<AbstractMesh> {
    const result = await SceneLoader.ImportMeshAsync('', '', url, this.scene);
    
    if (result.meshes.length === 0) {
      throw new Error(`DebugRenderer: No meshes loaded from ${url}`);
    }
    
    const rootMesh = result.meshes[0];
    
    // Mark all meshes as debug (no shadows, no collisions, etc.)
    const markDebug = (mesh: AbstractMesh) => {
      this.markAsDebugMesh(mesh);
      mesh.getChildMeshes().forEach(child => markDebug(child));
    };
    
    markDebug(rootMesh);
    
    this.meshes.add(rootMesh);
    return rootMesh;
  }
  
  public remove(mesh: AbstractMesh): void {
    if (this.meshes.has(mesh)) {
      // Dispose mesh and all children
      mesh.getChildMeshes().forEach(child => child.dispose());
      mesh.dispose();
      this.meshes.delete(mesh);
    }
    if (this.textPlanes.has(mesh)) {
      mesh.dispose();
      this.textPlanes.delete(mesh);
    }
  }

  /**
   * Create a billboard text label that always faces the camera.
   * @param text The text to display
   * @param position World position for the label
   * @param color Text color (default white)
   * @param fontSize Font size in pixels (default 48)
   * @returns The plane mesh with the text texture
   */
  public createTextLabel(text: string, position: Vector3, color?: Color3, fontSize: number = 48): AbstractMesh {
    const textColor = color || new Color3(1, 1, 1);
    
    // Create dynamic texture for text
    const textureWidth = 256;
    const textureHeight = 64;
    const texture = new DynamicTexture(`debugText_${text}_${Date.now()}`, { width: textureWidth, height: textureHeight }, this.scene, false);
    texture.hasAlpha = true;
    
    // Use DynamicTexture's built-in drawText method
    const colorString = `rgb(${Math.floor(textColor.r * 255)}, ${Math.floor(textColor.g * 255)}, ${Math.floor(textColor.b * 255)})`;
    texture.drawText(text, null, null, `bold ${fontSize}px Arial`, colorString, 'transparent', true, true);
    
    // Create plane to display texture
    const plane = MeshBuilder.CreatePlane(`debugTextPlane_${text}_${Date.now()}`, {
      width: 1.0,
      height: 0.25
    }, this.scene);
    
    plane.position.copyFrom(position);
    plane.billboardMode = AbstractMesh.BILLBOARDMODE_ALL; // Always face camera
    
    // Create material with texture
    const material = new StandardMaterial(`debugTextMat_${Date.now()}`, this.scene);
    material.diffuseTexture = texture;
    material.emissiveTexture = texture;
    material.disableLighting = true;
    material.useAlphaFromDiffuseTexture = true;
    material.backFaceCulling = false;
    plane.material = material;
    
    this.markAsDebugMesh(plane);
    
    this.textPlanes.add(plane);
    return plane;
  }

  /**
   * Create a camera frustum visualization
   * @param position Camera position
   * @param target Camera target/look-at point
   * @param name Camera name for label
   * @param color Visualization color (default cyan)
   * @returns Array of meshes representing the camera
   */
  public createCameraVisualization(position: Vector3, target: Vector3 | null, name: string, color?: Color3): AbstractMesh[] {
    const vizColor = color || new Color3(0, 1, 1); // Cyan
    const meshes: AbstractMesh[] = [];

    // Create camera body (small box)
    const body = this.createBox(position, new Vector3(0.3, 0.2, 0.4), vizColor, false);
    meshes.push(body);

    // Create lens (small sphere at front)
    const lensOffset = new Vector3(0, 0, -0.25);
    const lens = this.createSphere(position.add(lensOffset), 0.08, vizColor, false);
    meshes.push(lens);

    // If we have a target, draw direction arrow
    if (target) {
      const direction = target.subtract(position);
      if (direction.length() > 0.1) {
        const arrow = this.createArrow(position, direction.normalize(), Math.min(direction.length(), 2.0), vizColor);
        meshes.push(arrow);
      }
    }

    // Add text label above camera
    const labelPos = position.add(new Vector3(0, 0.5, 0));
    const label = this.createTextLabel(name, labelPos, vizColor, 32);
    meshes.push(label);

    return meshes;
  }
  
  /**
   * Create a wireframe clone of an existing mesh.
   * The clone follows the source mesh's world transform each frame.
   * @param sourceMesh The mesh to create a wireframe of
   * @param color Wireframe color
   * @returns The wireframe mesh
   */
  public createMeshWireframe(sourceMesh: AbstractMesh, color?: Color3): AbstractMesh {
    const wireColor = color || this.defaultWireframeColor;
    
    // Clone the geometry
    const wireframe = sourceMesh.clone(`debugWireframe_${sourceMesh.name}_${Date.now()}`, null) as AbstractMesh;
    if (!wireframe) {
      // Fallback to bounding box if clone fails
      return this.createBoundingBoxWireframe(sourceMesh, wireColor);
    }
    
    // Create wireframe material
    const material = new StandardMaterial(`debugWireframeMat_${Date.now()}`, this.scene);
    material.emissiveColor = wireColor;
    material.disableLighting = true;
    material.wireframe = true;
    wireframe.material = material;
    
    // Remove any child meshes from clone (we only want this mesh)
    wireframe.getChildMeshes().forEach(child => child.dispose());
    
    this.markAsDebugMesh(wireframe);
    wireframe.setEnabled(true);
    wireframe.isVisible = true;
    
    // Store reference to source mesh for transform sync
    wireframe.metadata = { 
      ...wireframe.metadata, 
      isDebug: true,
      noShadows: true,
      wireframeSource: sourceMesh 
    };
    
    this.meshes.add(wireframe);
    return wireframe;
  }
  
  /**
   * Update wireframe meshes to follow their source meshes.
   * Call this each frame when debug rendering is enabled.
   */
  public updateWireframes(): void {
    for (const mesh of this.meshes) {
      const source = mesh.metadata?.wireframeSource as AbstractMesh | undefined;
      if (source && !source.isDisposed()) {
        // Copy world matrix from source
        mesh.position.copyFrom(source.absolutePosition);
        if (source.absoluteRotationQuaternion) {
          mesh.rotationQuaternion = source.absoluteRotationQuaternion.clone();
        }
        mesh.scaling.copyFrom(source.absoluteScaling);
      }
    }
  }

  public dispose(): void {
    this.meshes.forEach(mesh => {
      mesh.getChildMeshes().forEach(child => child.dispose());
      mesh.dispose();
    });
    this.meshes.clear();
  }
  
  // Configuration getters/setters
  public getDefaultSplineMarkerInterval(): number {
    return this.defaultSplineMarkerInterval;
  }
  
  public setDefaultSplineMarkerInterval(meters: number): void {
    this.defaultSplineMarkerInterval = meters;
  }
  
  public getDefaultArrowLength(): number {
    return this.defaultArrowLength;
  }
  
  public setDefaultArrowLength(length: number): void {
    this.defaultArrowLength = length;
  }
}
